import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaCar, FaUsers, FaShieldAlt, FaHeadset, FaEye, FaBullseye, FaQuoteLeft } from 'react-icons/fa';
import founderImage from '../images/Founder image.jpeg';
import './About.css';

const About = () => {
  useEffect(() => {
    document.title = 'About Us | Kariyapperuma Car Sales';
    return () => { document.title = 'Kariyapperuma Car Sales'; };
  }, []);

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-overlay"></div>
        <div className="container">
          <motion.div
            className="about-hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="about-hero-title">Your Dream Begins Again with Us</h1>
            <p className="about-hero-subtitle">
              High-quality vehicles from a trusted importer — find your perfect car today
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section section">
        <div className="container">
          <div className="stats-grid">
            <motion.div
              className="stat-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <FaCar className="stat-icon" />
              <h3 className="stat-number">25+</h3>
              <p className="stat-label">Years in Business</p>
            </motion.div>
            <motion.div
              className="stat-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <FaUsers className="stat-icon" />
              <h3 className="stat-number">1000+</h3>
              <p className="stat-label">Happy Customers</p>
            </motion.div>
            <motion.div
              className="stat-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <FaShieldAlt className="stat-icon" />
              <h3 className="stat-number">100%</h3>
              <p className="stat-label">Quality Assured</p>
            </motion.div>
            <motion.div
              className="stat-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <FaHeadset className="stat-icon" />
              <h3 className="stat-number">24/7</h3>
              <p className="stat-label">Customer Support</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About the Company Section */}
      <section className="about-company section bg-light">
        <div className="container">
          <motion.div
            className="about-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">About Kariyapperuma Car Sales</h2>
            <p className="about-text">
              Kariyapperuma Car Sales has been a trusted name in Sri Lanka's automobile industry
              for over 25 years, specializing in importing high-quality vehicles and delivering
              honest, customer-focused service. Our commitment is to provide reliable vehicles
              while building long-lasting relationships based on trust, transparency, and
              professionalism. We take pride in carefully selecting every vehicle, ensuring
              quality and value for every customer who chooses us.
            </p>
          </motion.div>
        </div>
      </section>

      {/* About the Founder Section */}
      <section className="about-founder section">
        <div className="container">
          <motion.div
            className="founder-container"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="founder-image-wrapper">
              <img
                src={founderImage}
                alt="Ranil Kariyapperuma - Founder & Chairman"
                className="founder-image"
              />
            </div>
            <div className="founder-text-content">
              <h2 className="section-title">About the Founder</h2>
              <p className="about-text">
                Mr. Ranil Kariyapperuma is a Business Management degree holder and a lawyer by
                profession, with over 35 years of experience in business and entrepreneurship.
                He founded Kariyapperuma Car Sales with a clear vision — to offer Sri Lankans
                a trustworthy vehicle import service that always prioritizes quality, reliability,
                and customer satisfaction.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Founder's Message Section */}
      <section className="founders-message section bg-light">
        <div className="container">
          <motion.div
            className="message-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <FaQuoteLeft className="quote-icon" />
            <h2 className="section-title">Founder & Chairman's Message</h2>
            <div className="message-content">
              <p className="message-text">
                At Kariyapperuma Car Sales, our journey has always been guided by trust,
                responsibility, and strong customer relationships. When I began this business
                more than 25 years ago, my aim was not only to sell vehicles but to build a
                service where every customer feels respected, supported, and confident in
                their choice.
              </p>
              <p className="message-text">
                With over 35 years of experience in business, and as both a Business Management
                graduate and a lawyer by profession, I believe deeply in transparency, ethical
                practices, and delivering real value. Every vehicle we import is carefully
                selected to ensure quality, reliability, and suitability for the Sri Lankan market.
              </p>
              <p className="message-text">
                I am proud that many of our customers return to us again and again — and recommend
                us to their family and friends. That trust is our greatest achievement.
              </p>
              <p className="message-text">
                Thank you for being part of our journey. We look forward to helping you take
                the next step toward your dream vehicle.
              </p>
              <div className="message-signature">
                <strong>– Ranil Kariyapperuma</strong>
                <span>Founder & Chairman, Kariyapperuma Car Sales (pvt)Ltd  </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="vision-mission section">
        <div className="container">
          <div className="vision-mission-grid">
            <motion.div
              className="vm-card"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="vm-icon-wrapper">
                <FaEye className="vm-icon" />
              </div>
              <h3 className="vm-title">Our Vision</h3>
              <p className="vm-text">
                To be Sri Lanka's most trusted and customer-focused importer of high-quality vehicles.
              </p>
            </motion.div>

            <motion.div
              className="vm-card"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="vm-icon-wrapper">
                <FaBullseye className="vm-icon" />
              </div>
              <h3 className="vm-title">Our Mission</h3>
              <p className="vm-text">
                Our mission is to deliver quality vehicles and trustworthy service to every customer.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;